import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LongviewPaywall.module.scss"

/**
 * LongviewPaywall (`/longview`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/longview.md
 * The three prior ONBOARDING concepts — Aurora Vault (cosmic orbit), Abyss
 * (deep-sea descent) and Vault Door (steel crank) — are all DARK, illustrative/
 * skeuomorphic mood pieces that pitch privacy-as-wonder with a passive/admire
 * interaction and no behavioural conversion mechanic. This is none of that:
 *   - Layout:      a LIGHT "future projection" screen with a horizontal TIME
 *                  SCRUBBER (Today · 1yr · 5yr · A lifetime) and a growing tower
 *                  of dated memory cards that the whole stage ZOOMS OUT to fit —
 *                  literally "the long view." Not a hero+card, canvas, or door.
 *   - Interaction: INPUT-DRIVEN scrub of time; dragging forward materialises and
 *                  stacks real memory photos while a "kept" counter climbs.
 *                  (Priors: passive scroll, pointer parallax, crank.)
 *   - Register:    hopeful momentum / optimistic — "look how much life you'll
 *                  keep." Not cosmic awe, deep-sea calm, or steel tactility.
 *   - Art:         LIGHT ivory + PHOTOGRAPHIC (Unsplash life moments); brand blue
 *                  = the KEPT/protected accent, warm amber = the future horizon
 *                  glow. Not dark/skeuomorphic.
 *   - Value frame: FUTURE-SELF framing + endowed progress — "everything you'll
 *                  ever capture, kept for life." Forward permanence, not
 *                  privacy-as-mood.
 *
 * Honest & App Store compliant: the counter is clearly an estimate ("about", "at
 * a few a day") — a projection, not a promise. Real Premium features only
 * (automatic full-resolution backup, private/encrypted, on every device). No
 * data-loss claim anywhere ("always yours"). No fake countdown, no fake scarcity;
 * honest savings arithmetic. Yearly is the featured, default plan and the
 * scrubber's default dwell sits on the 1-year notch; monthly is small and
 * secondary. Photos are labelled as Unsplash example memories.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// ── The horizon stops the scrubber snaps to ────────────────────────────────
// `t` is a continuous 0→1 position along the timeline; the counter flows between
// stops while the headline snaps to the nearest one.
const STOPS = [
  {
    t: 0.0,
    kicker: "It starts today",
    title: "Your vault, empty",
    line: "Add your very first photo and it’s kept — private, full quality, from day one.",
    memories: 0,
    cards: 3,
  },
  {
    t: 0.36,
    kicker: "This time next year",
    title: "One year in",
    line: "A whole year of moments, every single one kept automatically.",
    memories: 1800,
    cards: 7,
  },
  {
    t: 0.68,
    kicker: "Five years on",
    title: "Five years in",
    line: "Chapters you’ll be so glad you never had to lose.",
    memories: 9000,
    cards: 11,
  },
  {
    t: 1.0,
    kicker: "The long view",
    title: "A lifetime in",
    line: "A whole life, kept whole — nothing faded, nothing left behind.",
    memories: 42000,
    cards: 15,
  },
] as const

const NOTCH_LABELS = ["Today", "1 yr", "5 yr", "Lifetime"] as const
const N_CARDS = 15

// piecewise-linear read of a per-stop field at continuous position t
function readAt(t: number, field: "memories" | "cards"): number {
  const c = clamp(t, 0, 1)
  for (let i = 0; i < STOPS.length - 1; i++) {
    const a = STOPS[i]
    const b = STOPS[i + 1]
    if (c >= a.t && c <= b.t) {
      const f = (c - a.t) / (b.t - a.t || 1)
      return a[field] + (b[field] - a[field]) * f
    }
  }
  return STOPS[STOPS.length - 1][field]
}

const nearestStopIndex = (t: number) => {
  let best = 0
  let bd = Infinity
  STOPS.forEach((s, i) => {
    const d = Math.abs(s.t - t)
    if (d < bd) {
      bd = d
      best = i
    }
  })
  return best
}

// deterministic tower geometry — cards climb upward from a bottom baseline,
// swaying gently and shrinking into the distance (the future rising away)
const GAP = 30 // px rise per card
const CARD_H = 150
const STAGE_USABLE = 322
const cardGeom = (i: number) => ({
  x: Math.sin(i * 0.9) * 20,
  rot: Math.sin(i * 1.7) * 3.4,
  scale: 1 - i * 0.012,
  y: -(i * GAP),
})

const Shield = ({ size = 18 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.2 12.1l2 2 3.6-3.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LongviewPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The time scrubber ─────────────────────────────────────────────────────
  const [t, setT] = useState(0)
  const tRef = useRef(0)
  const rafRef = useRef<number | undefined>(undefined)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [touched, setTouched] = useState(false)

  const setTNow = useCallback((v: number) => {
    tRef.current = v
    setT(v)
  }, [])

  const animateT = useCallback(
    (to: number, dur = 520) => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      const from = tRef.current
      const start = performance.now()
      const step = (now: number) => {
        const p = clamp((now - start) / dur, 0, 1)
        const e = 1 - Math.pow(1 - p, 3) // ease-out cubic
        setTNow(from + (to - from) * e)
        if (p < 1) rafRef.current = requestAnimationFrame(step)
        else rafRef.current = undefined
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [setTNow],
  )

  // Auto-demo once: glide from Today up to the 1-year notch (the yearly default),
  // teaching the scrub and landing on the featured plan's horizon.
  useEffect(() => {
    if (reduceMotion) {
      setTNow(STOPS[1].t)
      return
    }
    const id = window.setTimeout(() => animateT(STOPS[1].t, 1700), 480)
    return () => {
      window.clearTimeout(id)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const tFromClientX = (clientX: number) => {
    const el = trackRef.current
    if (!el) return tRef.current
    const r = el.getBoundingClientRect()
    return clamp((clientX - r.left) / r.width, 0, 1)
  }

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      setDragging(true)
      setTouched(true)
      setTNow(tFromClientX(e.clientX))
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        /* no-op */
      }
    },
    [setTNow],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return
      setTNow(tFromClientX(e.clientX))
    },
    [dragging, setTNow],
  )

  const onUp = useCallback(() => {
    if (!dragging) return
    setDragging(false)
    animateT(STOPS[nearestStopIndex(tRef.current)].t)
  }, [dragging, animateT])

  const goToStop = useCallback(
    (i: number) => {
      setTouched(true)
      animateT(STOPS[clamp(i, 0, STOPS.length - 1)].t)
    },
    [animateT],
  )

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      const cur = nearestStopIndex(tRef.current)
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault()
        goToStop(cur + 1)
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault()
        goToStop(cur - 1)
      }
    },
    [goToStop],
  )

  // ── Derived view state ─────────────────────────────────────────────────────
  const stopIdx = nearestStopIndex(t)
  const stop = STOPS[stopIdx]
  const memories = Math.round(readAt(t, "memories") / 10) * 10
  const visibleCards = Math.round(readAt(t, "cards"))
  const towerH = (Math.max(visibleCards, 1) - 1) * GAP + CARD_H
  const fit = clamp(STAGE_USABLE / towerH, 0.5, 1)

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.root}>
      <div className={styles.sky} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <Shield size={15} />
          </span>
          <span className={styles.brandText}>
            Private Photo Vault<em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <h1 className={styles.title}>
          Start today.
          <br />
          Keep it for life.
        </h1>
        <p className={styles.sub}>
          Drag through the years and watch what your vault becomes — every
          memory backed up, private, and kept in full quality.
        </p>
      </div>

      {/* ── THE PROJECTION STAGE ─────────────────────────────────────────── */}
      <div className={styles.readout}>
        <span className={styles.readKicker} key={`k${stopIdx}`}>
          {stop.kicker}
        </span>
        <span className={styles.readCount}>
          <strong>
            {memories === 0 ? "0" : `≈ ${memories.toLocaleString()}`}
          </strong>
          <span className={styles.readUnit}>
            {memories === 0 ? "memories — for now" : "memories, all kept"}
          </span>
        </span>
      </div>

      <div
        className={styles.stage}
        role="img"
        aria-label={`${stop.title}: about ${memories.toLocaleString()} memories kept`}
      >
        <span className={styles.horizonGlow} aria-hidden="true" />
        <div
          className={styles.tower}
          style={{ transform: `scale(${fit.toFixed(3)})` }}
        >
          {Array.from({ length: N_CARDS }).map((_, i) => {
            const g = cardGeom(i)
            const shown = i < visibleCards
            return (
              <figure
                key={i}
                className={`${styles.card} ${shown ? styles.cardIn : ""}`}
                style={{
                  zIndex: 100 - i,
                  transform: `translate(calc(-50% + ${g.x.toFixed(1)}px), ${g.y.toFixed(
                    1,
                  )}px) rotate(${g.rot.toFixed(2)}deg) scale(${g.scale.toFixed(3)})`,
                  transitionDelay: shown ? `${Math.min(i, 8) * 45}ms` : "0ms",
                }}
              >
                <img
                  src={`/longview/m${(i % N_CARDS) + 1}.jpg`}
                  alt=""
                  draggable={false}
                  loading="eager"
                  onError={(e) => {
                    ;(e.currentTarget as HTMLImageElement).style.opacity = "0"
                  }}
                />
                <span className={styles.cardKept} aria-hidden="true">
                  <Shield size={11} />
                </span>
              </figure>
            )
          })}
        </div>
        <span className={styles.baseline} aria-hidden="true" />
      </div>

      <p
        className={`${styles.stageLine} ${styles.stageLineFade}`}
        key={`l${stopIdx}`}
      >
        {stop.line}
      </p>

      {/* ── THE TIME SCRUBBER ────────────────────────────────────────────── */}
      <div className={styles.scrubWrap}>
        <div
          ref={trackRef}
          className={`${styles.track} ${dragging ? styles.trackDrag : ""} ${
            !touched ? styles.trackIdle : ""
          }`}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKey}
          role="slider"
          tabIndex={0}
          aria-label="How far into the future to look"
          aria-valuemin={0}
          aria-valuemax={STOPS.length - 1}
          aria-valuenow={stopIdx}
          aria-valuetext={stop.title}
        >
          <span className={styles.trackBase} aria-hidden="true" />
          <span
            className={styles.trackFill}
            style={{ width: `${(t * 100).toFixed(1)}%` }}
            aria-hidden="true"
          />
          {STOPS.map((s, i) => (
            <span
              key={i}
              className={`${styles.tick} ${t >= s.t - 0.02 ? styles.tickOn : ""}`}
              style={{ left: `${s.t * 100}%` }}
              aria-hidden="true"
            />
          ))}
          <span
            className={styles.thumb}
            style={{ left: `${(t * 100).toFixed(1)}%` }}
            aria-hidden="true"
          >
            <span className={styles.thumbDot} />
          </span>
        </div>
        <div className={styles.notches}>
          {NOTCH_LABELS.map((lbl, i) => (
            <button
              key={lbl}
              className={`${styles.notch} ${stopIdx === i ? styles.notchOn : ""}`}
              onClick={() => goToStop(i)}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <p className={styles.projNote}>
        A rough projection at a few photos a day — your vault fills at your own
        pace.
      </p>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyBody}>
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>A full year, kept</span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
            </span>
            <span className={styles.yearlyPerMo}>
              about {yearlyPerMonth}/mo, billed once a year
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Or go month to month</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <ul className={styles.reassure}>
        <li>
          <Shield size={13} /> Private &amp; encrypted
        </li>
        <li>Full quality</li>
        <li>Auto-backup</li>
        <li>Always yours</li>
      </ul>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Example memories from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Start keeping — free for 7 days"
                : "Start keeping — yearly"
              : "Start keeping — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default LongviewPaywall
